/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  HeaderMessageKey,
  getIsHeaderMessageClosed,
  setIsHeaderMessageClosed,
} from '@fai/friendli-suite/features/common/storage/header-message';
import {
  useServerlessEndpointsTeamPlan,
  useServerlessFreeCredit,
} from '@fai/friendli-suite/features/products/serverless-endpoints/plan/use-serverless-endpoints-team-plan';
import { usePaymentState } from '@fai/friendli-suite/features/settings/team-settings/use-payment';
import { useTeamRole } from '@fai/friendli-suite/features/settings/team-settings/use-role';
import HeaderMessage from '@fai/friendli-suite/views/layout/header/items/header-message';
import { openServerlessPricingModal } from '@fai/friendli-suite/views/serverless-endpoints/modals/pricing-modal';
import { openAddPaymentModal } from '@fai/friendli-suite/views/team-settings/billing/payment-method/add-payment-modal';

import GlobalHeaderMessage from './global';

function PlanTrialMessage() {
  const { t } = useTranslation();

  const headerMessageKey = HeaderMessageKey.ServerlessEndpointsTrial;

  const onClose = () => {
    setIsHeaderMessageClosed(headerMessageKey, true);
  };

  if (getIsHeaderMessageClosed(headerMessageKey)) return null;

  return (
    <HeaderMessage
      contentButton={
        <HeaderMessage.ContentActionButton handleOnClick={openServerlessPricingModal}>
          {t('serverless_endpoints.layout.view_pricing')}
        </HeaderMessage.ContentActionButton>
      }
      isCloseable={true}
      variant="INFO"
      onClose={onClose}
    >
      {t('serverless_endpoints.layout.you_now_receive_free_credits', {
        amount: 5,
      })}
    </HeaderMessage>
  );
}

function TeamOwnerWithNoPaymentMessage() {
  const { t } = useTranslation();

  return (
    <HeaderMessage
      contentButton={
        <HeaderMessage.ContentActionButton handleOnClick={openAddPaymentModal}>
          {t('serverless_endpoints.layout.add_payment_method')}
        </HeaderMessage.ContentActionButton>
      }
      variant="WARNING"
    >
      {t('serverless_endpoints.layout.to_keep_using_add_payment_method')}
    </HeaderMessage>
  );
}

function TeamMemberHasPaymentIssuesMessage() {
  const { t } = useTranslation();

  return (
    <HeaderMessage variant="WARNING">
      {t('serverless_endpoints.layout.serverless_endpoints_is_not_accessible')}
    </HeaderMessage>
  );
}

export default function ServerlessEndpointsHeaderMessage() {
  const { teamId = '' } = useParams();

  const { isTeamOwner, loading: roleLoading } = useTeamRole(teamId);
  const { isPaymentEmpty, isPaymentInvalid, loading: paymentLoading } = usePaymentState(teamId);
  const { plan: serverlessEndpointsPlan, loading: planLoading } =
    useServerlessEndpointsTeamPlan(teamId);
  const { isFreeCreditExist, loading: freeCreditLoading } = useServerlessFreeCredit(teamId);

  if (roleLoading || paymentLoading || planLoading || freeCreditLoading) {
    return null;
  }

  // If plan is ENTERPRISE, or has not tried trial yet, don't show message.
  if (
    serverlessEndpointsPlan === 'ENTERPRISE' ||
    serverlessEndpointsPlan === 'NONE_OWNER' ||
    serverlessEndpointsPlan === 'NONE_MEMBER'
  ) {
    return null;
  }

  // If plan is TRIAL, only show message about trial.
  if (serverlessEndpointsPlan === 'TRIAL' || isFreeCreditExist) {
    return <PlanTrialMessage />;
  }

  // If there is no payment method, show this for team owner.
  if (isTeamOwner && isPaymentEmpty && !isFreeCreditExist) {
    return <TeamOwnerWithNoPaymentMessage />;
  }

  // If there is an issue with a payment method, show this for team member.
  if (!isTeamOwner && isPaymentInvalid && !isFreeCreditExist) {
    return <TeamMemberHasPaymentIssuesMessage />;
  }

  return <GlobalHeaderMessage />;
}

ServerlessEndpointsHeaderMessage.displayName = 'ServerlessEndpointsHeaderMessage';
